import { useCallback, useMemo } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";

import { Button } from "#components/v2/Button";
import { Card } from "#components/v2/Card";
import { CopyButton } from "#components/v2/CopyButton";
import { Dialog, DialogTrigger, DialogHeader, DialogContent } from "#components/v2/Dialog";
import { ArrowsOutIcon, DownloadSimpleIcon } from "#icons";
import { cn } from "#utils";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";

import "./github-light.css";
import "./github-dark.css";
import "react-diff-view/style/index.css";
import { common, createLowlight } from "lowlight";

import { hcl } from "./hcl";

// Common languages supported by lowlight
type LowlightGrammar =
  | "javascript"
  | "typescript"
  | "python"
  | "java"
  | "c"
  | "cpp"
  | "csharp"
  | "php"
  | "ruby"
  | "go"
  | "rust"
  | "swift"
  | "kotlin"
  | "scala"
  | "bash"
  | "shell"
  | "powershell"
  | "sql"
  | "json"
  | "xml"
  | "html"
  | "css"
  | "scss"
  | "less"
  | "yaml"
  | "markdown"
  | "ini"
  | "toml"
  | "diff"
  | "plaintext"
  | "dockerfile"
  | "groovy"
  | "perl"
  | "r"
  | "wasm"
  | "hcl"
  | "terraform"
  | "tf"
  | string; // Allow custom grammars for extensibility

export interface CodeBlockProps {
  /** The new/current string content to display in the code block */
  code: string | null;
  /** The programming language for syntax highlighting. Defaults to "yaml" */
  grammar?: LowlightGrammar;
  /** Additional className for the code block */
  className?: string;
  /** Additional className for the code block container */
  codeBlockClassName?: string;
  /** Cap the visible height of the code area to roughly this many lines; overflow scrolls vertically. */
  maxLines?: number;
  /** File name with extension for download, e.g. "policy.yaml". When provided, displays a download button that saves the code with this filename */
  download?: string;
  /** Hide the "Code Preview" dialog trigger, leaving only the copy action. Defaults to false. */
  hideDialogPreview?: boolean;
}

// Built once for the whole module rather than per CodeBlock mount: loading the
// ~37 `common` grammars and registering HCL is stateless setup, and
// `highlight()` returns a fresh tree per call, so a single shared instance is
// safe and avoids repeating that work for every visible code block.
const lowlight = createLowlight(common);

lowlight.register({ hcl });
lowlight.registerAlias({ hcl: ["terraform", "tf"] });

export function CodeBlock({
  code,
  grammar = "yaml",
  className,
  codeBlockClassName,
  maxLines,
  download,
  hideDialogPreview = false,
}: CodeBlockProps) {
  const tree = useMemo(() => {
    // Fall back to plaintext for any grammar lowlight doesn't have registered,
    // which would otherwise throw "Unknown language" and crash the render.
    const safeGrammar = lowlight.registered(grammar) ? grammar : "plaintext";
    return lowlight.highlight(safeGrammar, code || "");
  }, [grammar, code]);

  const clampLines = maxLines && maxLines > 0 ? maxLines : undefined;

  const handleDownload = useCallback(() => {
    if (!download) return;

    const blob = new Blob([code || ""], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = download;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [code, download]);

  return (
    <Card
      className={cn(
        "overflow-x-auto whitespace-pre-wrap font-mono text-mono-medium flex gap-x-xs justify-between",
        className,
      )}
    >
      <div
        className={clampLines ? "flex-1 overflow-y-auto" : "overflow-hidden"}
        style={clampLines ? { maxHeight: `calc(${clampLines} * 1lh)` } : undefined}
      >
        <div className={cn("break-all", codeBlockClassName)}>
          {toJsxRuntime(tree, { Fragment, jsx, jsxs })}
        </div>
      </div>
      <div
        aria-label={`${grammar} code`}
        className="flex items-center gap-x-3xs self-start"
        role="code"
      >
        {download ? (
          <Button
            IconLeft={<DownloadSimpleIcon />}
            aria-label="Download file"
            onPress={handleDownload}
            variant="tertiary"
          />
        ) : null}
        <CopyButton value={code || ""} />
        {hideDialogPreview ? null : (
          <DialogTrigger>
            <Button IconLeft={<ArrowsOutIcon />} variant="tertiary" />
            <Dialog overlayClassName="z-[1300]" size="lg" isDismissable>
              <DialogHeader>Code Preview</DialogHeader>
              <DialogContent>
                <div className="whitespace-pre-wrap font-mono text-mono-medium">
                  {toJsxRuntime(tree, { Fragment, jsx, jsxs })}
                </div>
              </DialogContent>
            </Dialog>
          </DialogTrigger>
        )}
      </div>
    </Card>
  );
}
